import { $getRoot, LexicalEditor } from 'lexical'

import {
  DRAGGABLE_BLOCK_MENU_CLASSNAME,
  DIVIDER_TO_HALF,
  Downward,
  Indeterminate,
  SPACE,
  TARGET_LINE_HALF_HEIGHT,
  TEXT_BOX_HORIZONTAL_PADDING,
  Upward,
  POSITION_FOR_HIDE_ELEMENT,
} from './const'
import { Point } from '../../utils/point'
import { Rect } from '../../utils/rect'

const getCurrentIndex = (keysLength: number, prevIndex: number): number => {
  if (keysLength === 0) {
    return Infinity
  }
  if (prevIndex >= 0 && prevIndex < keysLength) {
    return prevIndex
  }

  return Math.floor(keysLength / DIVIDER_TO_HALF)
}

const getTopLevelNodeKeys = (editor: LexicalEditor): string[] => {
  return editor.getEditorState().read(() => $getRoot().getChildrenKeys())
}

interface GetCollapsedMarginsReturnType {
  marginTop: number
  marginBottom: number
}

const getCollapsedMargins = (elem: HTMLElement): GetCollapsedMarginsReturnType => {
  const getMargin = (element: Element | null, margin: 'marginTop' | 'marginBottom'): number =>
    element ? parseFloat(window.getComputedStyle(element)[margin]) : 0

  const { marginTop, marginBottom } = window.getComputedStyle(elem)
  const prevElemSiblingMarginBottom = getMargin(elem.previousElementSibling, 'marginBottom')
  const nextElemSiblingMarginTop = getMargin(elem.nextElementSibling, 'marginTop')
  const collapsedTopMargin = Math.max(parseFloat(marginTop), prevElemSiblingMarginBottom)
  const collapsedBottomMargin = Math.max(parseFloat(marginBottom), nextElemSiblingMarginTop)

  return { marginBottom: collapsedBottomMargin, marginTop: collapsedTopMargin }
}

interface GetBlockElementParams {
  anchorElem: HTMLElement
  editor: LexicalEditor
  event: MouseEvent
  prevIndex: number
  setPrevIndex: (index: number) => void
  useEdgeAsDefault?: boolean
}

export const getBlockElement = ({
  anchorElem,
  editor,
  event,
  prevIndex,
  setPrevIndex,
  useEdgeAsDefault = false,
}: GetBlockElementParams): HTMLElement | null => {
  const anchorElementRect = anchorElem.getBoundingClientRect()
  const topLevelNodeKeys = getTopLevelNodeKeys(editor)

  let blockElem: HTMLElement | null = null

  editor.getEditorState().read(() => {
    if (useEdgeAsDefault) {
      const [firstNode, lastNode] = [
        editor.getElementByKey(topLevelNodeKeys[0]),
        editor.getElementByKey(topLevelNodeKeys[topLevelNodeKeys.length - 1]),
      ]

      const [firstNodeRect, lastNodeRect] = [firstNode?.getBoundingClientRect(), lastNode?.getBoundingClientRect()]

      if (firstNodeRect && lastNodeRect) {
        if (event.y < firstNodeRect.top) {
          blockElem = firstNode
        } else if (event.y > lastNodeRect.bottom) {
          blockElem = lastNode
        }

        if (blockElem) {
          return
        }
      }
    }

    let index = getCurrentIndex(topLevelNodeKeys.length, prevIndex)
    let direction = Indeterminate

    while (index >= 0 && index < topLevelNodeKeys.length) {
      const key = topLevelNodeKeys[index]
      const elem = editor.getElementByKey(key)
      if (elem === null) {
        break
      }
      const point = new Point(event.x, event.y)
      const domRect = Rect.fromDOM(elem)
      const { marginTop, marginBottom } = getCollapsedMargins(elem)

      const rect = domRect.generateNewRect({
        bottom: domRect.bottom + marginBottom,
        left: anchorElementRect.left,
        right: anchorElementRect.right,
        top: domRect.top - marginTop,
      })

      const {
        result,
        reason: { isOnTopSide, isOnBottomSide },
      } = rect.contains(point)

      if (result) {
        blockElem = elem
        setPrevIndex(index)
        break
      }

      if (direction === Indeterminate) {
        if (isOnTopSide) {
          direction = Upward
        } else if (isOnBottomSide) {
          direction = Downward
        } else {
          // stop search block element
          direction = Infinity
        }
      }

      index += direction
    }
  })

  return blockElem
}

export const isOnMenu = (element: HTMLElement): boolean => {
  return !!element.closest(`.${DRAGGABLE_BLOCK_MENU_CLASSNAME}`)
}

export const setMenuPosition = (targetElem: HTMLElement | null, floatingElem: HTMLElement, anchorElem: HTMLElement): void => {
  if (!targetElem) {
    floatingElem.style.opacity = '0'
    floatingElem.style.transform = `translate(${POSITION_FOR_HIDE_ELEMENT}, ${POSITION_FOR_HIDE_ELEMENT})`
    return
  }

  const targetRect = targetElem.getBoundingClientRect()
  const targetStyle = window.getComputedStyle(targetElem)
  const floatingElemRect = floatingElem.getBoundingClientRect()
  const anchorElementRect = anchorElem.getBoundingClientRect()

  const top = targetRect.top + (parseInt(targetStyle.lineHeight, 10) - floatingElemRect.height) / DIVIDER_TO_HALF - anchorElementRect.top

  const left = SPACE

  floatingElem.style.opacity = '1'
  floatingElem.style.transform = `translate(${left}px, ${top}px)`
}

export const setDragImage = (dataTransfer: DataTransfer, draggableBlockElem: HTMLElement): void => {
  const { transform } = draggableBlockElem.style

  // Remove dragImage borders
  draggableBlockElem.style.transform = 'translateZ(0)'
  dataTransfer.setDragImage(draggableBlockElem, 0, 0)

  setTimeout(() => {
    draggableBlockElem.style.transform = transform
  })
}

export const setTargetLine = (targetLineElem: HTMLElement, targetBlockElem: HTMLElement, mouseY: number, anchorElem: HTMLElement): void => {
  const { top: targetBlockElemTop, height: targetBlockElemHeight } = targetBlockElem.getBoundingClientRect()
  const { top: anchorTop, width: anchorWidth } = anchorElem.getBoundingClientRect()

  const { marginTop, marginBottom } = getCollapsedMargins(targetBlockElem)
  let lineTop = targetBlockElemTop
  if (mouseY >= targetBlockElemTop) {
    lineTop += targetBlockElemHeight + marginBottom / DIVIDER_TO_HALF
  } else {
    lineTop -= marginTop / DIVIDER_TO_HALF
  }

  const top = lineTop - anchorTop - TARGET_LINE_HALF_HEIGHT
  const left = TEXT_BOX_HORIZONTAL_PADDING - SPACE

  targetLineElem.style.transform = `translate(${left}px, ${top}px)`
  targetLineElem.style.width = `${anchorWidth - (TEXT_BOX_HORIZONTAL_PADDING - SPACE) * DIVIDER_TO_HALF}px`
  targetLineElem.style.opacity = '.4'
}

export const hideTargetLine = (targetLineElem: HTMLElement | null): void => {
  if (targetLineElem) {
    targetLineElem.style.opacity = '0'
    targetLineElem.style.transform = `translate(${POSITION_FOR_HIDE_ELEMENT}, ${POSITION_FOR_HIDE_ELEMENT})`
  }
}
